import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import axios from "axios";
import { ChevronsUpDown, Building2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";

interface UserOption {
  id: number;
  name: string | null;
  email: string;
}

/**
 * 代理店がサインイン中の時に AppHeader 右上に出る切替 Combobox。
 * 表示対象は current_agency 配下に許可済みの user で、かつ親オーナー (parent_user_id IS NULL) のみ。
 */
export function AgencyUserSwitcher() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;

    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setLoading(true);
      axios
        .get('/agencies/users/search', { params: { q: query } })
        .then((res) => {
          setUsers(res.data?.users || []);
        })
        .catch(() => {
          setUsers([]);
        })
        .finally(() => setLoading(false));
    }, 250);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [open, query]);

  const handleSelect = (user: UserOption) => {
    if (switching) return;
    setSwitching(true);
    router.post(
      `/agencies/users/${user.id}/login_as`,
      {},
      {
        onFinish: () => {
          setSwitching(false);
          setOpen(false);
        },
      }
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="hidden md:inline-flex items-center gap-2 border-indigo-300 bg-indigo-50 text-indigo-900 hover:bg-indigo-100"
        >
          <Building2 className="h-3.5 w-3.5" />
          <span>アカウント切り替え</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="名前 or メールで検索..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                検索中...
              </div>
            ) : (
              <>
                <CommandEmpty>該当するユーザーが見つかりません</CommandEmpty>
                <CommandGroup>
                  {users.map((user) => (
                    <CommandItem
                      key={user.id}
                      value={`${user.id}-${user.email}`}
                      onSelect={() => handleSelect(user)}
                      disabled={switching}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {user.name || user.email}
                        </span>
                        {user.name && (
                          <span className="text-xs text-muted-foreground">
                            {user.email}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
